import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  hospitalAdminApi,
  type SarvamDnpPastAgeBuckets,
  type SarvamVoiceAiSettings,
} from "@/api/hospitalAdmin";
import { toast } from "sonner";

type Props = {
  settings: SarvamVoiceAiSettings | undefined;
  isLoading: boolean;
};

const BUCKET_LABELS: { key: keyof SarvamDnpPastAgeBuckets; label: string }[] = [
  { key: "days_0_30", label: "0–30 days (backlog)" },
  { key: "days_30_60", label: "30–60 days" },
  { key: "days_60_90", label: "60–90 days" },
  { key: "days_90_plus", label: "90+ days" },
];

export function SarvamDnpNotReachableSettingsCard({ settings, isLoading }: Props) {
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(true);
  const [rolloutMode, setRolloutMode] = useState<"TEST" | "LIVE">("TEST");
  const [agentAppId, setAgentAppId] = useState("");
  const [buckets, setBuckets] = useState<SarvamDnpPastAgeBuckets>({
    days_0_30: false,
    days_30_60: false,
    days_60_90: false,
    days_90_plus: false,
  });

  useEffect(() => {
    if (!settings) return;
    setEnabled(settings.dnpNotReachableRetryEnabled ?? true);
    setRolloutMode(settings.dnpNotReachableRolloutMode === "LIVE" ? "LIVE" : "TEST");
    setAgentAppId(
      settings.dnpNotReachableAgentAppId?.trim() ||
        settings.defaultAgentId?.trim() ||
        settings.agents?.[0]?.sarvamAppId ||
        ""
    );
    setBuckets(
      settings.dnpPastLeadsAgeBuckets ?? {
        days_0_30: false,
        days_30_60: false,
        days_60_90: false,
        days_90_plus: false,
      }
    );
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      await hospitalAdminApi.saveSarvamVoiceAiSettings({
        dnpNotReachableRetryEnabled: enabled,
        dnpNotReachableRolloutMode: rolloutMode,
        dnpNotReachableAgentAppId: agentAppId.trim() || undefined,
        dnpPastLeadsAgeBuckets: buckets,
      });
    },
    onSuccess: async () => {
      toast.success("Not Reachable (DNP) voice settings saved");
      await qc.invalidateQueries({ queryKey: ["sarvam-voice-ai-settings"] });
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        "Failed to save";
      toast.error(message);
    },
  });

  const toggleBucket = (key: keyof SarvamDnpPastAgeBuckets, checked: boolean) => {
    setBuckets((prev) => ({ ...prev, [key]: checked }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Voice AI — Not Reachable (Agent 1 DNP retry)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          When a lead&apos;s sub-status is <strong>Not Reachable</strong>, Agent 1 places up to{" "}
          <strong>9</strong> outbound calls at <strong>11:00</strong>, <strong>14:30</strong>, and{" "}
          <strong>20:00 IST</strong> (3 per day for 3 days). Each failed attempt increments the CRM{" "}
          DNP counter (e.g. 1/9). At 9/9 the lead is set to <strong>Lost · No response</strong>.
          Leads created within the last 30 days enroll automatically; enable age buckets below to
          include older Not Reachable backlogs.
        </p>
        {settings?.dnpNotReachableScheduleNote ? (
          <p className="text-xs text-muted-foreground">{settings.dnpNotReachableScheduleNote}</p>
        ) : null}

        <div className="flex items-start gap-3 rounded-lg border p-3">
          <Checkbox
            checked={enabled}
            onCheckedChange={(v) => setEnabled(v === true)}
            disabled={isLoading || saveMutation.isPending}
          />
          <div>
            <Label className="font-medium">DNP Sarvam retry ON</Label>
            <div className="text-xs text-muted-foreground mt-1">
              {settings?.enabled
                ? "Requires master Sarvam integration ON and reminder worker / API running."
                : "Turn on master Sarvam integration first."}
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Rollout mode</Label>
            <Select
              value={rolloutMode}
              onValueChange={(v) => setRolloutMode(v === "LIVE" ? "LIVE" : "TEST")}
              disabled={isLoading || saveMutation.isPending}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TEST">TEST (name contains Test)</SelectItem>
                <SelectItem value="LIVE">LIVE (all eligible NR leads)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Agent 1 app ID</Label>
            <Input
              value={agentAppId}
              onChange={(e) => setAgentAppId(e.target.value)}
              placeholder="Cosmetic / default Agent 1"
              disabled={isLoading || saveMutation.isPending}
            />
          </div>
        </div>

        <div className="space-y-3 rounded-lg border p-3">
          <Label className="font-medium">Past Not Reachable leads (by lead age)</Label>
          <p className="text-xs text-muted-foreground">
            Saving a newly enabled bucket schedules the 11:00 / 14:30 / 20:00 IST cadence for
            existing Not Reachable leads in that age range.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {BUCKET_LABELS.map(({ key, label }) => (
              <div key={key} className="flex items-center gap-2">
                <Checkbox
                  checked={buckets[key]}
                  onCheckedChange={(v) => toggleBucket(key, v === true)}
                  disabled={isLoading || saveMutation.isPending || !enabled}
                />
                <Label className="font-normal">{label}</Label>
              </div>
            ))}
          </div>
        </div>

        <Button
          type="button"
          onClick={() => saveMutation.mutate()}
          disabled={isLoading || saveMutation.isPending}
        >
          {saveMutation.isPending ? "Saving…" : "Save DNP voice settings"}
        </Button>
      </CardContent>
    </Card>
  );
}
